/**
 * Agent health and failover (ADR 0049 decision 5, step 2): when a coding agent fails a mission
 * for a reason of its own (signed out, quota, model, vendor outage, CLI missing, a process that
 * dies before it starts), the (machine, kind) pair cools down for a while and an 'auto' mission
 * moves to another agent on the same branch. A failure of the task (tests red, a refusal, a
 * budget stop, an approval that blocked it) never switches agents.
 *
 * Pure and browser-safe: the hub's scheduler keeps the records, placement reads `coolingOf`, the
 * console shows them.
 */
import type { Id, Session, Ts } from '../index.ts';
import { clip } from '../index.ts';
import type { FailureCause, SessionFailure } from './failures.ts';
import { FAILURE_CAUSE_LABEL, classifyFailure } from './failures.ts';
import { agentKindLabel } from './adapters.ts';
import { COOLING_BASE_MS, COOLING_MAX_MS } from './agent-cooling.ts';
export { COOLING_BASE_MS, COOLING_MAX_MS } from './agent-cooling.ts';

/** True when a failure is the agent's (switching agents may help), false when it is the task's. */
export function isAgentFailure(cause: FailureCause | null | undefined): boolean {
  return cause === 'auth' || cause === 'usage-limit' || cause === 'model-unavailable' || cause === 'api-error' || cause === 'crash' || cause === 'launch' || cause === 'environment' || cause === 'runtime';
}

/**
 * Words a run uses when its own environment stopped the work: its sandbox denied or blocked
 * something, file permissions blocked it, or it needs write access it does not have. Only that
 * clear wording counts, so a task blocked on a decision or a red test never matches.
 */
const ENVIRONMENT_BLOCK_RE = new RegExp(
  [
    String.raw`\bsandbox(?:ed)?\b[^.\n]{0,80}?\b(?:den(?:y|ies|ied)|block(?:s|ed)?|refus(?:es|ed)|prevent(?:s|ed)|prohibit(?:s|ed)?|not (?:allowed|permitted))\b`,
    String.raw`\b(?:den(?:y|ies|ied)|block(?:s|ed)?)\b[^.\n]{0,60}?\bsandbox\b`,
    String.raw`\b(?:blocked|denied)\b[^.\n]{0,40}?\b(?:file ?system|file) permissions?\b`,
    String.raw`\b(?:needs?|requires?)\b[^.\n]{0,40}?\bwrite access\b`,
    String.raw`\bpermissions? (?:den(?:y|ies|ied)|block(?:s|ed)?|prohibit(?:s|ed)?)\b`,
    // Words Codex runs used when its sandbox stopped the work (owner data, 20 Oct 2026: about 300 of
    // 485 Codex reports, none of the 94 Claude Code and Gemini CLI reports).
    String.raw`\bblocked by (?:the|its) (?:environment|sandbox)\b`,
    String.raw`\boutside (?:the|its) writable (?:sandbox|roots?)\b`,
    String.raw`\bgit(?: metadata)?\b[^.\n]{0,30}?\bis read-only\b`,
    // Git refused to write its own files ("Git denied index.lock creation", "denied Git metadata writes"), never a push a remote refused.
    String.raw`\bgit\b[^.\n]{0,12}?\bden(?:y|ies|ied)\b[^.\n]{0,30}?(?:\bindex\.lock\b|\bwrit\w*|\bcreat\w*|\bcommit\w*)`,
    String.raw`\bden(?:y|ies|ied)\b[^.\n]{0,12}?\bgit\b[^.\n]{0,20}?(?:\bwrit\w*|\bmetadata\b|\bindex\b)`,
    // Git's own files ("Git metadata permissions prevent committing", "Git index writes were denied",
    // "Git cannot create index.lock", "filesystem permissions prevent Git from writing").
    String.raw`\bgit(?:'s|’s)?\b[^.\n]{0,30}?\b(?:metadata|index(?:-lock)?)\b[^.\n]{0,12}?(?:\bpermis|\bwrites? (?:were|was|are|is) denied\b)`,
    String.raw`\b(?:cannot|could not|can't|can’t|unable to)\s+(?:create|write)\b[^.\n]{0,30}?\bindex\.lock\b`,
    String.raw`\bpermis\w*\b[^.\n]{0,20}?\bprevent\w*\b[^.\n]{0,20}?\bgit\b`,
    String.raw`\bdenied write access\b`,
    String.raw`\bblocked by (?:this |the |its )?(?:cache|file ?system|git(?: metadata| index)?|sandbox|session(?:'s|’s)?) permis`,
    // Package installs the environment refused ("registry access is denied", "denied dependency downloads").
    String.raw`\b(?:registry(?:\/store)?|network|dependency|package)\s+(?:access|downloads?|fetch(?:es)?)\s+(?:(?:is|are|was|were)\s+)?(?:den(?:y|ies|ied)|blocked)\b`,
    String.raw`\b(?:den(?:y|ies|ied)|blocked)\s+(?:(?:registry(?:\/store)?|network|dependency|package)\s+(?:access|downloads?|fetch\w*)|downloads?)\b`,
    String.raw`\bdownloads?\s+(?:(?:is|are|was|were)\s+(?:denied|blocked)\b|fail\w*\s+with\s+access[- ]denied\b)`,
    String.raw`\benvironment den(?:y|ies|ied)\b`,
    String.raw`\b(?:EACCES|EPERM)\b`,
  ].join('|'),
  'i',
);

/**
 * A session that ended with no error but says its environment blocked the work (a sandbox that
 * denies git metadata, a lock, the network): the agent's failure, not the task's, so placement
 * cools the pair and an 'auto' mission moves to another agent (ADR 0049 decisions 5 and 9). Null
 * for a session still running, or one whose last message does not say so.
 *
 * `finalText`: the session's whole final message when the caller has it (the mission's report,
 * Mission.report.text, 20 Oct 2026). A session's lastText is clipped, and Codex names the sandbox
 * at the end of a long report, so reading only lastText missed most of its environment blocks.
 */
export function environmentFailureOf(s: Pick<Session, 'status' | 'lastText' | 'lastActivity'>, finalText?: string | null): SessionFailure | null {
  if (s.status !== 'ended') return null;
  for (const text of [finalText, s.lastText]) {
    if (!text) continue;
    const m = ENVIRONMENT_BLOCK_RE.exec(text);
    if (!m) continue;
    const start = Math.max(text.lastIndexOf('.', m.index) + 1, text.lastIndexOf('\n', m.index) + 1);
    const end = text.slice(m.index).search(/[.\n]/);
    const said = text.slice(start, end < 0 ? undefined : m.index + end).replace(/[`*_]/g, '').replace(/\s+/g, ' ').trim();
    return { cause: 'environment', summary: clip(`Its environment blocked the work: ${said.replace(/^./, (c) => c.toLowerCase())}.`, 200), detail: clip(text, 500), at: s.lastActivity };
  }
  return null;
}

/**
 * The mission report's text that environmentFailureOf may read for this session (20 Oct 2026):
 * the report read from that session (or, with `legacy`, one read before reports named their
 * session), and never one whose acceptance criteria are all met. A finished run that describes
 * a sandbox or a permission fix in passing is not blocked, so its mission never fails over.
 */
export function reportTextFor(
  report: { text: string; acceptance?: readonly { met: boolean }[]; sessionId?: Id } | null | undefined,
  sessionId: Id | null | undefined,
  legacy = false,
): string | null {
  if (!report?.text) return null;
  if (report.sessionId ? report.sessionId !== sessionId : !legacy) return null;
  if (report.acceptance?.length && report.acceptance.every((a) => a.met)) return null;
  return report.text;
}

/**
 * How one (machine, kind) pair is doing, kept by the scheduler while it cools down. With
 * `accountId` it is one account of that kind on the machine (ADR 0071 decision 9, amended 20 Oct
 * 2026): a subscription that ran out while a fallback account took its work. Such a record cools
 * only that account; the kind keeps running on other accounts.
 */
export interface AgentHealth {
  machineId: Id;
  kind: string;
  /** The launch account that ran out; absent: the kind itself. Additive, 20 Oct 2026. */
  accountId?: string;
  /** Placement skips the pair until then. */
  coolUntil: Ts;
  /** Agent failures in a row (a healthy run clears the record). */
  strikes: number;
  lastCause: FailureCause;
  /** The last failure in one plain sentence. */
  lastMessage: string;
  at: Ts;
}

/** One agent failure a queue entry went through, newest last. */
export interface AgentFailover {
  kind: string;
  machineId: Id;
  cause: FailureCause;
  /** One plain sentence: what failed and why. */
  summary: string;
  /** Its process ended with the agent that ran it (SessionFailure.interrupted): no strike, the same kind may continue. */
  interrupted?: boolean;
  at: Ts;
  /**
   * The work moved to another account of the same agent instead of another agent: the account
   * that ran out and the per-token account that took over (ids and names, never a secret).
   * Additive, 20 Oct 2026.
   */
  account?: { from: { id: string; name: string }; to: { id: string; name: string } };
}


/** The cooling period after `strikes` agent failures in a row: 15 minutes, doubled per strike, at most 4 hours. */
export function coolingPeriodMs(strikes: number): number {
  const n = Math.max(1, Math.floor(strikes));
  return Math.min(COOLING_MAX_MS, COOLING_BASE_MS * 2 ** Math.min(n - 1, 16));
}

/** The record after one more agent failure of the pair (`prev` is its current record, if any). */
export function strike(prev: AgentHealth | null | undefined, f: { machineId: Id; kind: string; accountId?: string; cause: FailureCause; message: string }, now: Ts): AgentHealth {
  const strikes = (prev?.strikes ?? 0) + 1;
  return { machineId: f.machineId, kind: f.kind, ...(f.accountId ? { accountId: f.accountId } : {}), coolUntil: now + coolingPeriodMs(strikes), strikes, lastCause: f.cause, lastMessage: f.message, at: now };
}

/** Kinds cooling down right now, by machine: machine id to kind to the time the cooling ends. */
export type CoolingMap = Readonly<Record<Id, Readonly<Record<string, Ts>>>>;

/** Kinds cooling down now. Records of one account are left out (`accountCoolUntil`): the kind runs on its other accounts. */
export function coolingOf(health: Iterable<AgentHealth>, now: Ts): Record<Id, Record<string, Ts>> {
  const out: Record<Id, Record<string, Ts>> = {};
  for (const h of health) {
    if (h.coolUntil <= now || h.accountId) continue;
    (out[h.machineId] ??= {})[h.kind] = h.coolUntil;
  }
  return out;
}

/** When one account of a kind on a machine stops cooling down after it ran out, or null when it is not cooling. */
export function accountCoolUntil(health: Iterable<AgentHealth>, machineId: Id, kind: string, accountId: string, now: Ts): Ts | null {
  for (const h of health) if (h.accountId === accountId && h.machineId === machineId && h.kind === kind && h.coolUntil > now) return h.coolUntil;
  return null;
}

const QUOTA_RE = /usage limit|(?:hit|reached) your (?:(?:usage|session|weekly|daily) )?limit|(?:5-hour|session|weekly|daily|opus|sonnet) limit (?:was )?reached|credit balance|out of (?:credits|extra usage)|insufficient[_ ]quota|exceeded your (?:current )?quota/i;

/**
 * Whether an agent failure means the account itself ran out (ADR 0071 decision 9, amended 20 Oct
 * 2026): a subscription's usage limit, or credit or quota that is used up. Only this moves work
 * to a per-token fallback account. A short API rate limit (429, rate_limit_error) does not: it
 * passes in seconds, and paying for it is not what the person asked for. Neither does a sign-in
 * that expired (the person signs in again), a vendor outage (the per-token API shares it) or
 * anything the task did. `text`: the failure's summary and detail, or the machine's words.
 */
export function accountRanOut(cause: FailureCause | null | undefined, text: string): boolean {
  return cause === 'usage-limit' && QUOTA_RE.test(text);
}

const NOT_FOUND_RE = /\bwas not found on this machine\b|\bis not installed\b|\bnot on PATH\b/i;
const SIGNED_OUT_RE = /\bnot signed in\b|\bsigned out\b|\bsign in again\b|\bauth_required\b/i;
// The ACP adapter says "exited" (OpenCode, 22 Oct 2026): unmatched, the agent was never cooled and every fix restarted on it.
const EARLY_EXIT_RE = /\b(?:stopped|exited) before the session began\b/i;
/** The hub's words ("(its own ceiling, set on that machine)") and the agent's ("This machine's own ceiling refuses ..."). */
const CEILING_REFUSAL_RE = /\(its own ceiling\b|\bceiling\b[^.]{0,60}\brefuses?\b/i;

/** A ceiling entry that refuses a session mode or permission mode, whatever the agent kind. */
const CEILING_MODE_RE = /\bsession\.start:(?:mode:|terminal\b|headless\b)/i;

/** A start refused for its folder: one that already exists, or one that is missing. */
const FOLDER_RE = /\b(?:folder|directory|worktree|path)\b[^.]{0,200}?\b(?:already exists|does not exist|was not found|is missing)\b/i;

/** A start the machine did not answer in time (the hub's 'timeout' error): it is retried once before it counts. */
export function isStartTimeout(text: string, code?: unknown): boolean {
  return code === 'timeout' || /\bdid not answer in time\b/i.test(text);
}

/**
 * The permission mode a start may ask a machine for (QA-127): bypassPermissions only where the
 * machine's ceiling allows it, else the default mode, so the org's approval rules answer instead
 * of a start the ceiling refuses.
 */
export function permissionModeFor<M extends string>(deny: readonly string[] | null | undefined, mode: M | null | undefined): M | null {
  if (!mode) return null;
  if (mode === 'bypassPermissions' && deny?.includes('session.start:mode:bypassPermissions')) return null;
  return mode;
}

/** Did a machine's own ceiling refuse this start (the hub's pre-check or the agent's)? */
export function isCeilingRefusal(text: string): boolean {
  return CEILING_REFUSAL_RE.test(text);
}

/**
 * Why a start the machine refused was the agent's fault, or null when it was not (a policy gate,
 * a missing folder, a conflict, a bad request). The machine's words decide: a CLI that is not
 * installed, an agent that is not signed in, a quota or a model error, a process that exited
 * before the session began. A gate's refusal (403) is never the agent's, with one exception
 * (20 Oct 2026): a refusal by the machine's own ceiling is 'launch' for that (machine, kind)
 * pair, so the pair cools down and the work moves to another agent, never the task's failure.
 * A ceiling that refuses only a mode (session.start:mode:bypassPermissions, :terminal, :headless)
 * refuses that start for every kind: it is not the agent's, so the kind does not cool for the
 * starts in other modes it would run.
 */
export function startFailureCause(status: number, text: string): FailureCause | null {
  if (isCeilingRefusal(text)) return CEILING_MODE_RE.test(text) ? null : 'launch';
  if (status === 403 || status === 404 || !text.trim()) return null;
  // QA-127: a folder that exists or is missing is the start's, never the agent's.
  if (FOLDER_RE.test(text)) return null;
  // Folder paths and item keys are names, not the machine's words: "mission-orgu-401-..." read as an HTTP 401 (signed out).
  const words = text.replace(/\S*[\\/]\S*/g, ' ').replace(/\b[A-Za-z]+-\d+\b/g, ' ');
  if (NOT_FOUND_RE.test(words)) return 'launch';
  if (SIGNED_OUT_RE.test(words)) return 'auth';
  if (EARLY_EXIT_RE.test(words)) return 'crash';
  // The session classifier reads the rest (quota, model, API errors); its fallback 'crash' means "no match".
  const f = classifyFailure({ status: 'ended', lastText: '', apiError: words, at: 0 });
  return f && f.cause !== 'crash' && isAgentFailure(f.cause) ? f.cause : null;
}

/** "Codex failed (usage limit)" */
export function failoverTitle(f: Pick<AgentFailover, 'kind' | 'cause'>): string {
  return `${agentKindLabel(f.kind)} failed (${FAILURE_CAUSE_LABEL[f.cause].toLowerCase()})`;
}

/** The line a switched mission carries: "Codex failed (usage limit). The work continues on the same branch." */
export function failoverNote(f: Pick<AgentFailover, 'kind' | 'cause'>, branch?: string | null): string {
  return `${failoverTitle(f)}. The work continues on the same branch${branch ? `, ${branch}` : ''}.`;
}

/** Every reason an entry's agents failed, for the message of an entry that cannot switch again. */
export function failoverReasons(list: readonly Pick<AgentFailover, 'kind' | 'cause' | 'summary'>[]): string {
  return list.map((f) => `${failoverTitle(f)}: ${f.summary.replace(/\.$/, '')}`).join('; ');
}

const FAILOVER_NOTE_RE = /^.+ failed \([^)]+\)\. The work continues on the same branch(?:, .+)?\.$/;

/** accountFallbackNote (launch-accounts.ts). */
const ACCOUNT_NOTE_RE = /^.+ reached its usage limit\. The work continues on .+, billed per token\.$/;

/** The failover lines in a launch's note (`failoverNote`, `accountFallbackNote`), for the mission's starts: the rest is what a person wrote. */
export function failoverLinesOf(note: string | null | undefined): string[] {
  return (note ?? '').split('\n').map((l) => l.trim()).filter((l) => FAILOVER_NOTE_RE.test(l) || ACCOUNT_NOTE_RE.test(l));
}

/** One failure an entry went through, as its queue row lists it. */
export function failoverLine(f: Pick<AgentFailover, 'kind' | 'cause' | 'summary' | 'account'>): string {
  return f.account ? `${f.account.from.name} reached its usage limit. ${f.account.to.name} took over, billed per token.` : `${failoverTitle(f)}: ${f.summary}`;
}
